import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from "common";
import { useTranslation } from "react-i18next";
import {
  Typography,
  TextField,
  Box,
  InputAdornment,
  IconButton,
  ButtonGroup,
  Button,
} from "@mui/material";
import AlertDialog from "../components/AlertDialog";
import moment from "moment/min/moment-with-locales";
import { useNavigate, useLocation } from "react-router-dom";
import { getLangKey } from "common/src/other/getLangKey";
import CustomTable from "components/Table/CustomTable";
import useSearch from "hooks/useSearch";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { Add, ArrowForward } from "@mui/icons-material";
import { ReactComponent as DeleteIcon } from "../assets/img/trash.svg";
import CustomSwitch from "components/CustomSwitch";

export default function CarsList() {
  const { t } = useTranslation();
  const settings = useSelector((state) => state.settingsdata.settings);
  const userdata = useSelector((state) => state.usersdata);
  const auth = useSelector((state) => state.auth);
  const { updateUserCar, editCar } = api;
  const [driversObj, setDriversObj] = useState();

  const carlistdata = useSelector((state) => state.carlistdata);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [role, setRole] = useState(null);
  const { state } = useLocation();
  const [currentPage, setCurrentPage] = useState(null);
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });
  const { filteredData, handleClearSearch, handleSearchChange, searchQuery } =
    useSearch(carlistdata?.cars);

  useEffect(() => {
    setCurrentPage(state?.pageNo);
  }, [state]);

  const HandalePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDelete = (oldData) =>
    settings.AllowCriticalEditsAdmin
      ? new Promise((resolve, reject) => {
          setTimeout(() => {
            if (oldData.active) {
              reject();
              alert(t("active_car_delete"));
            } else {
              resolve();
              dispatch(editCar(oldData, "Delete"));
            }
          }, 600);
        })
      : new Promise((resolve) => {
          setTimeout(() => {
            resolve();
            alert(t("demo_mode"));
          }, 600);
        });

  useEffect(() => {
    if (role !== "driver" && userdata.users) {
      let arr = userdata.users.filter(
        (user) =>
          user.usertype === "driver" &&
          ((role === "fleetadmin" &&
            user.fleetadmin &&
            user.fleetadmin === auth.profile.uid) ||
            role === "admin")
      );
      let obj = {};
      for (let i = 0; i < arr.length; i++) {
        let user = arr[i];
        obj[user.id] = `${user.firstName} ${user.lastName} (${
          settings.AllowCriticalEditsAdmin ? user.mobile : t("hidden_demo")
        }) ${settings.AllowCriticalEditsAdmin ? user.email : t("hidden_demo")}`;
      }
      setDriversObj(obj);
    }
  }, [
    userdata.users,
    settings.AllowCriticalEditsAdmin,
    role,
    auth.profile.uid,
    t,
  ]);

  useEffect(() => {
    if (auth.profile && auth.profile.usertype) {
      setRole(auth.profile.usertype);
    }
  }, [auth.profile]);

  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: "" });
  };

  const columns = [
    {
      title: t("createdAt"),
      field: "createdAt",
      editable: "never",
      defaultSort: "desc",
      render: (rowData) =>
        rowData?.createdAt
          ? moment(rowData.createdAt).format("DD MMM YYYY")
          : "N/A",
    },
    {
      title: t("car_type"),
      field: "carType",
      render: (rowData) =>
        rowData?.carType ? t(getLangKey(rowData.carType)) : null,
    },
    { title: t("vehicle_reg_no"), field: "vehicleNumber" },
    { title: t("vehicle_model_name"), field: "vehicleMake" },
    { title: t("vehicle_model_no"), field: "vehicleModel" },
    { title: t("other_info"), field: "other_info" },
    {
      title: t("active_status"),
      field: "active",
      editable: "never",
      type: "boolean",
      initialEditValue: true,
      render: (rowData) => (
        <CustomSwitch
          disabled
          checked={rowData?.active}
          onChange={() => handelActiveStatus(rowData)}
        />
      ),
    },
    {
      title: t("approved"),
      field: "approved",
      editable: role === "admin" ? "never" : "always",
      type: "boolean",
      initialEditValue: true,
      render: (rowData) => (
        <CustomSwitch
          checked={rowData?.approved}
          onChange={() => handelApproved(rowData)}
          disabled={
            !(
              settings.AllowCriticalEditsAdmin &&
              settings.carApproval &&
              role === "admin"
            )
          }
        />
      ),
    },
    {
      title: t("actions"),
      field: "",
      render: (rowData) => (
        <ButtonGroup
          variant="contained"
          disableElevation
          aria-label="button group of cars table action buttons"
          sx={{
            border: "0.6px solid #D5D5D5",
            borderRadius: "0.7rem",
            overflow: "hidden",
          }}
        >
          <Button
            sx={{
              backgroundColor: "#f8f8f8",

              "&:hover": {
                backgroundColor: "#e1e1e1",
              },
            }}
            onClick={() =>
              navigate(`/cars/editcar/${rowData.id}`, {
                state: { pageNo: currentPage },
              })
            }
          >
            <ArrowForward style={{ fontSize: "20px", color: "#4B545A" }} />
          </Button>
          <Button
            sx={{ backgroundColor: "#f8f8f8" }}
            onClick={() => handleDelete(rowData)}
          >
            <DeleteIcon style={{ fontSize: "24px" }} />
          </Button>
        </ButtonGroup>
      ),
    },
  ];

  const handelApproved = (rowData) => {
    if (settings.carApproval && role === "admin") {
      dispatch(
        updateUserCar(rowData.driver, {
          carApproved: !rowData.approved,
        })
      );

      dispatch(editCar({ ...rowData, approved: !rowData.approved }, "Update"));
    }
  };

  const handelActiveStatus = (rowData) => {
    const updateData = { ...rowData, active: !rowData.active };
    return updateData;
  };

  return carlistdata.loading ? (
    <CircularLoading />
  ) : (
    <div ref={rootRef}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: "600",
          fontSize: "30px",
          mt: 3,
          color: "#333",
        }}
      >
        {t("cars_title")}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          mb: 2,
          gap: 4,
        }}
      >
        <TextField
          placeholder={t("search")}
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{
            width: "250px",
            height: "42px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "20px",
              bgcolor: "#fff",
              height: "100%",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={t("clear_search")}
                  onClick={handleClearSearch}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <Button
          startIcon={<Add />}
          aria_label={t("add_car")}
          onClick={() =>
            navigate(`/cars/editcar`, { state: { pageNo: currentPage } })
          }
          variant="contained"
          sx={{
            backgroundColor: "#4F913C !important",
            color: "white",
            borderRadius: "30px !important",
            padding: "8px 24px",
            fontSize: "16px",
            fontWeight: "700",
            boxShadow: "none",
            whiteSpace: "nowrap",
            textTransform: "capitalize",
            transition: "all 0.3s ease-in-out",
            "&:hover": {
              backgroundColor: "#B3DBA8",
            },
          }}
        >
          {t("add_car")}
        </Button>
      </Box>
      <CustomTable columns={columns} filteredData={filteredData} />
      <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>
        {commonAlert.msg}
      </AlertDialog>
    </div>
  );
}
